import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown,
  Package,
  DollarSign,
  Calendar,
  Download
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { clientPortalService } from '../../services/clientPortal.service';
import { SalesPerformanceCard } from '../../components/insights/SalesPerformanceCard';
import { toast } from 'sonner';

const Reports = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');

  useEffect(() => {
    loadData();
  }, [dateRange]);

  const loadData = async () => {
    try {
      const ordersData = await clientPortalService.getOrders();
      setOrders(ordersData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  // Filter orders by date range
  const filterByDateRange = (orders) => {
    const now = new Date();
    const daysAgo = new Date();
    daysAgo.setDate(now.getDate() - parseInt(dateRange));
    return orders.filter(o => new Date(o.created_at) >= daysAgo);
  };

  const filteredOrders = filterByDateRange(orders);
  const deliveredOrders = filteredOrders.filter(o => o.status === 'delivered');

  // Calculate metrics
  const totalSpent = filteredOrders.reduce((sum, o) => sum + o.total_amount, 0);
  const totalOrders = filteredOrders.length;
  const avgOrderValue = totalOrders > 0 ? totalSpent / totalOrders : 0;

  // Calculate top products
  const productStats = {};
  filteredOrders.forEach(order => {
    order.items?.forEach(item => {
      if (!productStats[item.product_name]) {
        productStats[item.product_name] = {
          name: item.product_name,
          quantity: 0,
          spent: 0,
          orders: 0
        };
      }
      productStats[item.product_name].quantity += item.quantity;
      productStats[item.product_name].spent += item.subtotal;
      productStats[item.product_name].orders += 1;
    });
  });

  const topProducts = Object.values(productStats)
    .sort((a, b) => b.spent - a.spent)
    .slice(0, 10);

  // Calculate monthly breakdown
  const monthlyData = {};
  filteredOrders.forEach(order => {
    const month = new Date(order.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    if (!monthlyData[month]) {
      monthlyData[month] = { orders: 0, spent: 0 };
    }
    monthlyData[month].orders += 1;
    monthlyData[month].spent += order.total_amount;
  });

  // Simple bar chart representation
  const maxSpent = Math.max(...Object.values(monthlyData).map(m => m.spent), 1);

  const handleExport = () => {
    toast.info('Export feature coming soon!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="reports-loading">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500"></div>
          <p className="mt-4 text-gray-600">Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
          <p className="text-sm text-gray-600 mt-1">Your ordering trends and analytics</p>
        </div>
        <div className="flex gap-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-40" data-testid="date-range-select">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="total-spent-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Spent</p>
                <p className="text-2xl font-bold text-gray-900">${totalSpent.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="total-orders-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Orders</p>
                <p className="text-2xl font-bold text-gray-900">{totalOrders}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="avg-order-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Avg Order Value</p>
                <p className="text-2xl font-bold text-gray-900">${avgOrderValue.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="delivered-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <BarChart3 className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Delivered</p>
                <p className="text-2xl font-bold text-gray-900">{deliveredOrders.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sold against ordered — guaranteed-returns accounts only */}
      <SalesPerformanceCard />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Spending Over Time */}        <Card data-testid="spending-chart-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-amber-500" />
              Spending Over Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(monthlyData).length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No data for selected period
              </div>
            ) : (
              <div className="space-y-3">
                {Object.entries(monthlyData).reverse().map(([month, data]) => (
                  <div key={month}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium">{month}</span>
                      <span className="text-gray-600">${data.spent.toFixed(2)}</span>
                    </div>
                    <div className="h-6 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full transition-all"
                        style={{ width: `${(data.spent / maxSpent) * 100}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{data.orders} orders</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Products */}
        <Card data-testid="top-products-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-amber-500" />
              Top Products
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topProducts.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No data for selected period
              </div>
            ) : (
              <div className="space-y-3">
                {topProducts.map((product, index) => (
                  <div 
                    key={product.name} 
                    className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg"
                  >
                    <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{product.name}</p>
                      <p className="text-xs text-gray-500">
                        {product.quantity} packs • {product.orders} orders
                      </p>
                    </div>
                    <p className="font-semibold text-amber-600">${product.spent.toFixed(2)}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Order Frequency Insights */}
      <Card data-testid="insights-card">
        <CardHeader>
          <CardTitle className="text-lg">Insights</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-amber-600">
                {totalOrders > 0 ? (parseInt(dateRange) / totalOrders).toFixed(1) : '-'}
              </p>
              <p className="text-sm text-gray-600 mt-1">Days between orders (avg)</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-amber-600">
                {topProducts[0]?.name?.split(' ').slice(0, 2).join(' ') || '-'}
              </p>
              <p className="text-sm text-gray-600 mt-1">Most ordered product</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-amber-600">
                {Object.keys(productStats).length}
              </p>
              <p className="text-sm text-gray-600 mt-1">Unique products ordered</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Reports;
